// Keyboard shortcuts for the office shell. Pages are iframes with their own keystrokes, so
// utilities.js hands these keys up from inside a page (SHELL_KEY messages); here they also work
// in the shell itself. Never while typing, and not while a dialog or the New Work Order form is up.
//   /  or Ctrl+K   search (Ctrl+K opens All pages in the Pinned menu style: railStyles.js)
//   N              new work order
//   G then a key   go to a page (see GO)
//   ?              this list

import { escapeHTML } from './utilities';

// G then …: rail ids (hubs open the page last used in them). Only those this person has are listed.
export const GO = [
  { key: 'd', label: 'Dashboard', id: 'hub-dashboard' },
  { key: 'w', label: 'Work Orders', id: 'open-work-button' },
  { key: 'h', label: 'Haul / Launch', id: 'haul-launch-open-button' },
  { key: 's', label: 'Scheduler', id: 'scheduler-open-button' },
  { key: 'e', label: 'Equipment', id: 'hub-equipment' },
  { key: 'm', label: 'Moorings', id: 'hub-moorings' },
  { key: 'k', label: 'Dock', id: 'hub-dock' },
  { key: 'y', label: 'Yard Map', id: 'map-open-button' },
  { key: 'i', label: 'Inventory', id: 'inventory-menu-open-button' },
  { key: 'f', label: 'Fuel', id: 'hub-fuel' },
  { key: 'c', label: 'Customers', id: 'customers-open-button' },
  { key: 'b', label: 'Inbox', id: 'hub-inbox' },
  { key: 'l', label: 'Daily Log', id: 'daily-log-button' },
  { key: 't', label: 'Time clock', id: 'timeclock-open-button' },
];
const G_WINDOW_MS = 1500;

let hooks = {};
let gAt = 0;

const usable = id => { const el = document.getElementById(id); return el && !el.classList.contains('feature-off') ? el : null; };
const typing = () => {
  const a = document.activeElement;
  return !!a && (a.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName));
};
/** Something modal is up: the New Work Order form, the Customize editor, All pages, any aria-modal dialog. */
function busy() {
  if (document.getElementById('Add')?.style.display === 'flex') return true;
  return [...document.querySelectorAll('[aria-modal="true"]')].some(d => d.offsetParent !== null && !d.closest('[hidden]'));
}

function focusSearch() {
  const input = document.getElementById('railSearchInput');
  if (!input) return;
  // On a phone the rail is the slide-out menu: open it first.
  if (input.offsetParent === null) document.getElementById('mobile-menu-button')?.click();
  input.focus();
  input.select();
}

/** One key, from the shell or forwarded from a page. Returns true when it was a shortcut. */
function handle(key, { ctrlK = false } = {}) {
  if (helpOpen() && key !== '?') return false;
  if (ctrlK) {
    if (document.body.classList.contains('rail-style-pinned')) hooks.openAllPages?.();
    else focusSearch();
    return true;
  }
  if (busy()) return false;
  if (gAt && Date.now() - gAt < G_WINDOW_MS) {
    gAt = 0;
    const go = GO.find(x => x.key === key);
    const el = go && usable(go.id);
    if (el) { el.click(); return true; }
    return false;
  }
  gAt = 0;
  if (key === 'g') { gAt = Date.now(); return true; }
  if (key === 'n') { hooks.createNew?.('wo'); return true; }
  if (key === '?') { toggleHelp(); return true; }
  return false;
}

/* ---------- ? : the list ---------- */

const helpOpen = () => !!document.getElementById('shell-keys-help');

function toggleHelp() {
  const open = document.getElementById('shell-keys-help');
  if (open) { open.remove(); return; }
  const kbd = k => `<kbd>${escapeHTML(k)}</kbd>`;
  const goRows = GO.filter(x => usable(x.id)).map(x => `<div class="sk-row"><span>${kbd('G')} ${kbd(x.key.toUpperCase())}</span><span>${escapeHTML(x.label)}</span></div>`).join('');
  const wrap = document.createElement('div');
  wrap.id = 'shell-keys-help';
  wrap.className = 'sk-scrim';
  wrap.innerHTML = `<div class="sk-panel" role="dialog" aria-modal="true" aria-labelledby="sk-title">
    <div class="sk-head"><h2 id="sk-title">Keyboard shortcuts</h2><button type="button" class="sk-x" aria-label="Close">✕</button></div>
    <div class="sk-cols">
      <section><h3>Anywhere</h3>
        <div class="sk-row"><span>${kbd('/')} or ${kbd('Ctrl')} ${kbd('K')}</span><span>Find anything</span></div>
        <div class="sk-row"><span>${kbd('N')}</span><span>New work order</span></div>
        <div class="sk-row"><span>${kbd('?')}</span><span>This list</span></div>
        <h3>In search</h3>
        <div class="sk-row"><span>${kbd('↑')} ${kbd('↓')} ${kbd('Enter')}</span><span>Open a result</span></div>
        <div class="sk-row"><span>${kbd('Shift')} ${kbd('Enter')}</span><span>New work order for that customer or boat</span></div>
        <div class="sk-row"><span>${kbd('Esc')}</span><span>Close</span></div>
      </section>
      <section><h3>Go to (press G, then)</h3>${goRows}</section>
    </div>
    <p class="sk-note">Shortcuts don't fire while you're typing in a box. In the Pinned menu style, ${kbd('Ctrl')} ${kbd('K')} opens All pages.</p>
  </div>`;
  document.body.appendChild(wrap);
  wrap.addEventListener('click', e => { if (e.target === wrap || e.target.closest('.sk-x')) wrap.remove(); });
  wrap.querySelector('.sk-x').focus();
}

/* ---------- init ---------- */

/** hooks: { createNew(kind), openAllPages() } */
export function initShellKeys(h = {}) {
  hooks = h;
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && helpOpen()) { document.getElementById('shell-keys-help').remove(); return; }
    if (e.altKey || typing()) return;
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') return; // shellNav / railStyles handle it here
    if (e.ctrlKey || e.metaKey) return;
    const key = e.key === '?' ? '?' : e.key.length === 1 ? e.key.toLowerCase() : '';
    if (key && handle(key)) e.preventDefault();
  });
  // Keys pressed inside a page (utilities.js).
  window.addEventListener('message', (e) => {
    if (e.origin !== window.location.origin || e.data?.type !== 'SHELL_KEY') return;
    handle(String(e.data.key || ''), { ctrlK: !!e.data.ctrlK });
  });
}
